import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import LessonIntro from "../../components/LessonPlayer/LessonIntro";
import CongratulationsView from "../../components/lessons/CongratulationsView";
import CertificateView from "../../components/lessons/CertificateView";
import LevelCompletionView from "../../components/lessons/LevelCompletionView";
import LessonCardView from "./LessonCardView";
import { useLessonPlayerHook } from "./useLessonPlayerHook";
import SoftLockModal from "../../components/lessons/SoftLockModal";
import { findNextIncompleteLessonInLevel, findRemainingLevel } from "./lessonCompletionUtils";
import CompletionSaveStatus from "./CompletionSaveStatus";
import { useFollowOnCourse } from "./useFollowOnCourse";

const LessonPlayer: React.FC = () => {
  const navigate = useNavigate();
  const { state: routeState } = useLocation();
  const { lessonId } = useParams();
  const {
    lesson,
    currentCard,
    isLoading,
    showLessonContent,
    showCongratulations,
    showCertificate,
    isGeneratingCertificate,
    lessonViewRef,
    certificateRef,
    score,
    totalCards,
    currentCardIndex,
    canProceed,
    startLesson,
    nextCard,
    previousCard,
    completeCard,
    skipLesson,
    completionVariant,
    handleCardStateChange,
    handleDownloadCertificate,
    handleShareOnLinkedIn,
    handleShareCertificate,
    goHome,
    isLessonCompleting,
    showSoftLockModal,
    softLockMessage,
    closeSoftLockModal,
    lessonErrorCode,
    recoveryAttempt, recoveryRestored, recoveryError, recoveryPersistent, runRecovery, discardRecovery,
    returnToRecovery, signInForRecovery, completionContext, completionWasReview,
    certificateStatus, refreshCertificateStatus,
  } = useLessonPlayerHook();

  const context = completionContext;
  const currentLessons = context?.level.lessons;
  const sorted = [...(currentLessons || [])].sort((a, b) => a.order - b.order);
  // Reviews retain the ordinary unit screen except the final ordered unit replay.
  const showLevelCompletion = showCongratulations && !!context && context.currentLevel.isCompleted &&
    (!completionWasReview || sorted[sorted.length - 1]?.id === lessonId);
  const nextLevel = findRemainingLevel(context?.master.levels, context?.currentLevel.levelNumber);
  const followOnCourse = useFollowOnCourse(context?.master.id, showLevelCompletion && context?.master.isCompleted === true);
  const isOwnRecovery = !!recoveryAttempt && recoveryAttempt.lessonId === lessonId;
  const savedRecovery = isOwnRecovery && recoveryAttempt?.phase === "saved";
  const completionVisible = showCongratulations || savedRecovery;
  const [contextWaitExpired, setContextWaitExpired] = useState(false);
  useEffect(() => {
    setContextWaitExpired(false);
    if (!completionVisible || context) {return;}
    const timer = window.setTimeout(() => setContextWaitExpired(true), 8000);
    return () => window.clearTimeout(timer);
  }, [lessonId, completionVisible, context]);
  const contextFailed = !!recoveryError && (!recoveryAttempt || isOwnRecovery);
  const continueLoading = !context && !contextFailed && !contextWaitExpired;
  const recoveryVisible = isOwnRecovery ? !!recoveryError || recoveryRestored
    : recoveryAttempt ? recoveryAttempt.phase === "pending" : !!recoveryError;
  const recoveryView = recoveryVisible && <CompletionSaveStatus
    error={recoveryError} persistent={recoveryPersistent} busy={isLessonCompleting}
    saved={savedRecovery} otherLesson={!!recoveryAttempt && !isOwnRecovery}
    onRetry={runRecovery} onSignIn={signInForRecovery} onReturn={returnToRecovery} onDiscard={discardRecovery}
  />;

  const handleContinue = () => {
    if (continueLoading) {return;}
    if (!context) {
      // Route hints choose a recovery destination, never completion authority.
      const masterId = typeof routeState?.masterCourseId === "string" ? routeState.masterCourseId : null;
      const levelId = typeof routeState?.levelId === "string" ? routeState.levelId : lesson?.course_id;
      navigate(masterId ? `/master-courses/${encodeURIComponent(masterId)}${levelId ? `/levels/${encodeURIComponent(levelId)}` : ""}` : "/courses");
      return;
    }
    const nextLesson = findNextIncompleteLessonInLevel(context.level.lessons, lessonId);
    if (nextLesson) { navigate(`/lesson/${nextLesson.id}`, { replace: true }); return; }
    if (nextLevel) {
      navigate(`/master-courses/${context.master.id}/levels/${nextLevel.id}`);
    } else { navigate(`/master-courses/${context.master.id}`); }
  };

  if (lessonErrorCode === "course_retired") {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center">
        {recoveryView}
        <h1 className="text-2xl font-semibold text-gray-800 mb-3">
          This course has been retired
        </h1>
        <p className="text-gray-600 mb-6">
          Your completed progress is preserved. Any certificates remain available in
          Certificates; eligible certified credentials remain in your Passport.
        </p>
        <button
          onClick={() => navigate("/courses")}
          className="text-primary hover:underline"
        >
          Back to courses
        </button>
      </div>
    );
  }

  if ((isLoading || !lesson) && !showSoftLockModal && !recoveryVisible && !showCongratulations) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading unit...</div>
      </div>
    );
  }

  const showLesson =
    !!lesson && !showCongratulations && !showCertificate && !showLevelCompletion &&
    !(recoveryVisible && isOwnRecovery && recoveryAttempt?.phase === "pending");

  return (
    <div
      data-testid="lesson-player"
      className="px-4 py-4 sm:px-8 sm:py-8 lg:px-16 lg:py-10 bg-background flex flex-col items-center justify-center gap-6 w-full min-h-[calc(100vh-60px)] m-auto relative"
    >
      {recoveryView}
      {showLesson && lesson && (
        <>
          {!showLessonContent && (
            <LessonIntro
              lesson={lesson}
              onStart={startLesson}
              lessonRef={lessonViewRef}
              onSkip={skipLesson}
              isSkipping={isLessonCompleting}
            />
          )}

          {showLessonContent && (
            <div
              ref={lessonViewRef}
              className="w-full mx-auto flex flex-col gap-6 shadow-sm"
            >
              <LessonCardView
                currentCard={currentCard}
                lessonId={lesson.id}
                totalCards={totalCards}
                currentCardIndex={currentCardIndex}
                canProceed={canProceed}
                onNextCard={nextCard}
                onPreviousCard={previousCard}
                onCompleteCard={completeCard}
                onCardStateChange={handleCardStateChange}
                isLessonCompleting={isLessonCompleting}
              />
            </div>
          )}
        </>
      )}

      {showLevelCompletion && context && (
        <LevelCompletionView
          levelTitle={context.currentLevel.title}
          levelNumber={context.currentLevel.levelNumber}
          masterCourseId={context.master.id}
          masterCourseTitle={context.master.title}
          onContinue={handleContinue}
          completionKind={context.master.isCompleted ? "course" : "module"}
          nextLevelTitle={nextLevel?.title}
          nextCourse={followOnCourse}
          showCertificateAction={context.certificateEntitled === true}
          certificateStatus={certificateStatus}
          onCheckCertificate={refreshCertificateStatus}
        />
      )}

      {(showCongratulations || savedRecovery) && !showLevelCompletion && (
        <CongratulationsView
          levelTitle={context?.currentLevel.title}
          levelNumber={context?.currentLevel.levelNumber}
          masterCourseId={context?.master.id}
          masterCourseTitle={context?.master.title}
          currentLessonId={lessonId}
          variant={completionVariant}
          showCertificateAction={context?.certificateEntitled === true}
          onContinue={handleContinue}
          continueLoading={continueLoading}
        />
      )}

      {showCertificate && (
        <CertificateView
          courseTitle={lesson?.title || "Course"}
          userName="Student" // This would come from user context
          score={score}
          courseId="" // This would come from course context
          lessonId={lesson?.id || ""}
          certificateRef={certificateRef}
          isGeneratingCertificate={isGeneratingCertificate}
          onDownloadCertificate={handleDownloadCertificate}
          onShareOnLinkedIn={handleShareOnLinkedIn}
          onShareCertificate={handleShareCertificate}
          onBack={goHome}
        />
      )}

      <SoftLockModal
        open={showSoftLockModal}
        message={
          softLockMessage ||
          "This unit is not currently available. Return to your course to check your access."
        }
        onClose={closeSoftLockModal}
      />
    </div>
  );
};

export default LessonPlayer;
